/**
 * THE BOAT'S ONE PIECE OF STATE, WRITTEN TO THE SAVE.
 *
 * CL2-R2-G: round 2 said "the seam is ready on my side: one string, versioned, with a
 * parse that cannot throw" - and that described half the work. The critic measured the
 * other half: `SaveEnvelope`'s top-level keys are exactly `forward, sim, v, wardrobe`,
 * `Session`'s only state-setting method is `setTarget`, and the exported save string for a
 * session with an outstanding order contained no order. There was no slot AND no write
 * path. A player could auto-sail to a place, moor, close the tab, and come back to find
 * the boat back on its natural track somewhere else entirely - the one deliberate act of
 * the session being the only thing the save did not keep.
 *
 * -- WHERE IT IS WRITTEN, AND WHY THERE ---------------------------------------
 *
 * `src/sim` is another lane's file and this round may not edit it, so the order rides in
 * the envelope's `forward.envelope` bag under ONE namespaced key. That bag is documented
 * as carrying fields from NEWER builds, which is the opposite direction, and using it for
 * a current-version field is a WORKAROUND AND IS LABELLED ONE. What makes it safe rather
 * than merely convenient, all four MEASURED against the shipped modules
 * (tools/lanes/core-loop/persistence-probe.test.ts):
 *
 *   - It survives the round trip. `exportSave` -> `importSave` returns the key byte for
 *     byte, with no notes and with `sim` untouched.
 *   - It is a fixed point of `normaliseEnvelope`, so normalising our own output does not
 *     accumulate copies.
 *   - It lands in exactly ONE place. Writing `voyage` at the top level of the envelope and
 *     writing it inside `forward.envelope` produce the IDENTICAL export string, because
 *     the normaliser moves an unknown top-level key into that bag. So there is no
 *     configuration with two copies to disagree.
 *   - It cannot smuggle a rate. The bag refuses every key in `RATE_SHAPED_KEYS`, which is
 *     the same list `entitlements.ts` owns, so the monetisation law is enforced on this
 *     path by sim-core's own scanner and not by a promise from this lane.
 *
 * -- THE CROSS-LANE ASK, SIZED PROPERLY THIS TIME -----------------------------
 *
 * CL2-R2-G's real finding was that round 2 mis-sized this ask as "one field". It is four
 * things, and CONTRACT.md section 7 item 4 makes the save format owner-blocking, so it is
 * stated here in full rather than discovered during the gate:
 *
 *   1. a field:      `readonly voyage: string` on `SaveEnvelope`
 *   2. a mutator:    `setVoyageOrder(text: string, nowMs: number): void` on `Session`,
 *                    writing immediately the way `setTarget` does, because it is also a
 *                    deliberate player action
 *   3. a normaliser: `voyage: strField(r['voyage'], '', MAX_ORDER_LENGTH)` and `'voyage'`
 *                    added to `KNOWN_ENVELOPE_KEYS`
 *   4. a migration:  v1 -> v2 lifting `forward.envelope.voyage` into the new field, so
 *                    every save written by THIS build keeps its destination
 *
 * Until those land, this file is the whole write path, and `voyageOrderStorage` below is
 * how it gets onto disk without any change to src/sim at all.
 */

import type { SaveEnvelope, SaveStorage } from './sim-bridge';
import { exportSave, normaliseEnvelope } from './sim-bridge';
import type { VoyageOrder } from './voyage';
import { DRIFT_ORDER, parseOrder, serializeOrder } from './voyage';

/**
 * THE ONE KEY. Namespaced by the layer that owns it, so a future first-class `voyage`
 * field on the envelope and this bag entry can coexist through one migration instead of
 * silently becoming two copies of one fact.
 */
export const VOYAGE_SAVE_KEY = 'voyage.order';

/** Prefix of an exported save string, mirrored from sim-core's format. */
const SAVE_PREFIX_SEPARATOR = '.';

/**
 * The order a save is carrying, or a drifting boat.
 *
 * Total: an envelope with no order, a malformed order, an order naming a place this build
 * has never heard of - every one of them is a drifting boat, which is the state a new save
 * starts in. `parseOrder` already guarantees that and this adds no second opinion.
 */
export function orderFromEnvelope(envelope: SaveEnvelope | null | undefined): VoyageOrder {
  if (envelope === null || envelope === undefined) return DRIFT_ORDER;
  const bag = envelope.forward?.envelope;
  if (bag === null || bag === undefined) return DRIFT_ORDER;
  return parseOrder(bag[VOYAGE_SAVE_KEY]);
}

/** The same read, from a raw parsed object rather than a typed envelope. */
function orderFromRaw(raw: unknown): VoyageOrder {
  if (typeof raw !== 'object' || raw === null) return DRIFT_ORDER;
  const record = raw as Record<string, unknown>;
  const forward = record['forward'];
  const bag =
    typeof forward === 'object' && forward !== null
      ? ((forward as Record<string, unknown>)['envelope'] as Record<string, unknown> | undefined)
      : undefined;
  // Accept the top-level spelling too: it is what the normaliser consumes, and a save
  // hand-edited by a player will use it because it is the readable one.
  return parseOrder(bag?.[VOYAGE_SAVE_KEY] ?? record[VOYAGE_SAVE_KEY]);
}

/** Split an exported save string into its prefix, stamp and body. null if it is not one. */
function splitSave(text: string): { readonly body: string } | null {
  const firstDot = text.indexOf(SAVE_PREFIX_SEPARATOR);
  const secondDot = firstDot < 0 ? -1 : text.indexOf(SAVE_PREFIX_SEPARATOR, firstDot + 1);
  if (firstDot < 0 || secondDot < 0) return null;
  return { body: text.slice(secondDot + 1) };
}

/**
 * Return `text` with `order` written into it, re-checksummed.
 *
 * NEVER THROWS AND NEVER LOSES THE SAVE. Any text this cannot understand is returned
 * unchanged, so the worst outcome is a save without the boat's destination - which is
 * exactly what round 2 shipped, and is survivable - rather than a save that will not open.
 *
 * The checksum and the canonical form are sim-core's own: this calls `normaliseEnvelope`
 * and `exportSave` rather than re-implementing either. A second implementation of a save
 * format is a second save format.
 */
export function withVoyageOrder(text: string, order: VoyageOrder): string {
  if (typeof text !== 'string') return text;
  const split = splitSave(text);
  if (split === null) return text;
  try {
    const parsed: unknown = JSON.parse(split.body);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return text;
    // Written at the TOP LEVEL and normalised into the bag by sim-core's own normaliser,
    // which is what guarantees one copy rather than two: an existing bag entry is
    // overwritten by the merge, never added beside.
    const withOrder = {
      ...(parsed as Record<string, unknown>),
      forward: stripBagKey((parsed as Record<string, unknown>)['forward']),
      [VOYAGE_SAVE_KEY]: serializeOrder(order),
    };
    return exportSave(normaliseEnvelope(withOrder));
  } catch {
    return text;
  }
}

/** The forward bag with any previous copy of the order removed, so the merge cannot double. */
function stripBagKey(forward: unknown): unknown {
  if (typeof forward !== 'object' || forward === null) return forward;
  const record = forward as Record<string, unknown>;
  const bag = record['envelope'];
  if (typeof bag !== 'object' || bag === null) return forward;
  const rest: Record<string, unknown> = { ...(bag as Record<string, unknown>) };
  delete rest[VOYAGE_SAVE_KEY];
  return { ...record, envelope: rest };
}

/** The order an exported save string is carrying. Total; drift for anything unreadable. */
export function orderFromText(text: unknown): VoyageOrder {
  if (typeof text !== 'string') return DRIFT_ORDER;
  const split = splitSave(text);
  if (split === null) return DRIFT_ORDER;
  try {
    return orderFromRaw(JSON.parse(split.body));
  } catch {
    return DRIFT_ORDER;
  }
}

/**
 * A `SaveStorage` that writes the boat's order into every save the simulation writes.
 *
 * This is the whole trick, and it is deliberately the smallest possible one: sim-core's
 * session already writes an export string to a port on every autosave, so the order is
 * spliced into that string on its way through. Reads are handed back untouched - the
 * order comes back off the envelope, because the normaliser has already put it in the bag
 * - so this decorator has no opinion about loading at all and cannot corrupt it.
 *
 * `currentOrder` is a function rather than a value because the session's order changes
 * under it; a snapshot taken at open would write a stale destination for ever.
 */
export function voyageOrderStorage(inner: SaveStorage, currentOrder: () => VoyageOrder): SaveStorage {
  return {
    read: (key: string) => inner.read(key),
    write: (key: string, value: string) => {
      inner.write(key, withVoyageOrder(value, currentOrder()));
    },
    remove: (key: string) => inner.remove(key),
  };
}
